import { computeOnceKey } from '#pplx/frontend/framework/instrumentation/internal/computeOnceKey.js';
import { mergeScopeChain } from '#pplx/frontend/framework/instrumentation/internal/mergeScopeChain.js';
import { ScopeContext } from '#pplx/frontend/framework/instrumentation/internal/ScopeContext.js';
import { TransportContext } from '#pplx/frontend/framework/instrumentation/internal/TransportContext.js';
import { useCallback, useContext, useRef } from 'react';
/**
 * Hook for firing instrumentation events. The transport context defaults to a
 * no-op pair, so `track()` and `configure()` are always safe to call regardless
 * of whether an `InstrumentationRoot` is mounted upstream.
 */
export function useInstrumentation() {
    const scope = useContext(ScopeContext);
    const { config, configure } = useContext(TransportContext);
    // The dedup Set is scoped to this hook instance (i.e., this component
    // instance). This preserves backwards compatibility with how impression
    // tracking has historically been deduplicated in the codebase.
    const seenOnce = useRef(new Set());
    const track = useCallback((options) => {
        // Guard against empty-name events, which most analytics backends
        // silently drop. Fail closed — skip the merge and the send so the
        // no-op is visible to the developer (no event in the network tab)
        // rather than disappearing into the backend.
        if (options.event === '') {
            return;
        }
        // Disabled transport (consent pending/denied, dormant root): drop
        // without consuming `once`, so impressions re-arm and deliver on the
        // first enabled render after `configure()` flips the state.
        if (!config.enabledState.enabled) {
            return;
        }
        const event = mergeScopeChain(scope, config, options);
        const onceKey = computeOnceKey(options.event, options.once);
        if (onceKey !== null) {
            if (seenOnce.current.has(onceKey)) {
                return;
            }
            seenOnce.current.add(onceKey);
        }
        config.send(event);
    }, [scope, config]);
    const impression = useCallback((options) => {
        const { key, ...rest } = options;
        track({ ...rest, once: key !== undefined ? { enabled: true, key } : true });
    }, [track]);
    return { track, impression, configure, enabled: config.enabledState.enabled };
}
//# sourceMappingURL=useInstrumentation.js.map